import React, { useState } from 'react';
import { Container, Row, Col, Card, Badge, ListGroup, Button, Alert, Nav, Tab } from 'react-bootstrap';
import Accordion from "react-bootstrap/Accordion";
import 'bootstrap/dist/css/bootstrap.min.css';

interface Props {
  fullName: string;
}


const PersonalizedPreCheckinExperience = (props: Props) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedItems, setSelectedItems] = useState<any>({
    roomItems: [],
    activities: [],
    restaurants: [],
    carRental: null
  });
  const displayName = props.fullName?.trim() || 'Guest';

  const guestProfile = {
    name: "Ms. Jane Doe",
    checkIn: "June 25, 2025",
    nights: 7,
    accessibility: "Wheelchair access required",
    medical: "Type 1 Diabetes - Insulin dependent",
    dietary: ["Vegetarian", "Gluten-free"],
    interests: ["History", "Architecture", "Museums"],
    preferences: ["Italian cuisine", "Japanese cuisine", "Spa treatments"]
  };

  const currentEvents = [
    {
      id: 1,
      name: "Zurich Art Weekend 2025",
      dates: "June 13-15, 2025",
      description: "75+ exhibitions and 150+ events featuring art across 70+ wheelchair accessible venues",
      relevance: "Art and cultural exhibitions perfect for history and architecture interests",
      accessibility: "All venues wheelchair accessible",
      bookingUrl: "#",
      price: "Free admission"
    },
    {
      id: 2,
      name: "Food Zurich Festival",
      dates: "June 12-22, 2025 (Ongoing during your stay)",
      description: "100+ culinary events with extensive vegetarian and gluten-free options",
      relevance: "Perfect for exploring Swiss vegetarian cuisine",
      accessibility: "Multiple accessible venues citywide",
      bookingUrl: "#",
      price: "Various pricing"
    },
    {
      id: 3,
      name: "St. Gallen Festival",
      dates: "June 21 - July 6, 2025",
      description: "Classical concerts in UNESCO World Heritage cathedral",
      relevance: "Historical architecture in magnificent baroque setting",
      accessibility: "Cathedral fully wheelchair accessible",
      bookingUrl: "#",
      price: "From CHF 25"
    }
  ];

  const restaurantRecommendations = [
    {
      id: 1,
      name: "Granò",
      cuisine: "Italian",
      features: ["100% gluten-free restaurant", "Certified vegetarian kitchen", "Wheelchair accessible"],
      address: "Badenerstrasse 298, 8004 Zürich",
      specialties: ["Gluten-free pasta", "Vegetarian pizza", "Traditional tiramisu"],
      distance: "1.2 km from hotel",
      rating: 4.8,
      priceRange: "$$",
      reservationNeeded: true
    },
    {
      id: 2,
      name: "Restaurant Bimi",
      cuisine: "Japanese",
      features: ["Wheelchair accessible", "Terrace dining", "Authentic Japanese cuisine"],
      address: "Seefeldstrasse 25, 8008 Zürich",
      specialties: ["Traditional sushi", "Vegetarian Japanese options", "Modern fusion"],
      distance: "0.8 km from hotel",
      rating: 4.6,
      priceRange: "$$$",
      reservationNeeded: true
    },
    {
      id: 3,
      name: "The Artisan",
      cuisine: "International Vegetarian",
      features: ["Organic ingredients", "Vegetarian specialties", "Eco-friendly practices"],
      address: "Wipkingen, Zurich",
      specialties: ["Smoked Eggplant", "Tomato Tarte Tatin", "Artisan Veg Burger"],
      distance: "2.1 km from hotel",
      rating: 4.7,
      priceRange: "$$",
      reservationNeeded: false
    }
  ];

  const activityRecommendations = [
    {
      id: 1,
      name: "Essential Old Town Zurich Wheelchair Accessible Tour",
      type: "Cultural Tour",
      duration: "1.5 hours",
      features: ["English-speaking guide", "Step-free routes", "Historic architecture focus"],
      description: "Explore St. Peter's Church, Bahnhofstrasse, and historic buildings with wheelchair-friendly routes",
      accessibility: "Fully wheelchair accessible",
      price: "CHF 35 per person",
      timeSlots: ["10:00 AM", "2:00 PM", "4:00 PM"]
    },
    {
      id: 2,
      name: "Swiss Museum of Transport",
      type: "Museum Visit",
      duration: "3-4 hours",
      features: ["Wheelchair accessible", "English audio guides", "Transportation history"],
      description: "Interactive exhibits showcasing Swiss transport innovation and history",
      accessibility: "Wheelchair accessible, loan wheelchairs available",
      price: "CHF 32 (Reduced rate with disability ID)",
      timeSlots: ["9:00 AM - 6:00 PM daily"]
    },
    {
      id: 3,
      name: "Accessible Swiss Alps Experience at Engstligenalp",
      type: "Nature Experience",
      duration: "Full day",
      features: ["Cable car access", "4.5km accessible trail", "Mountain panorama"],
      description: "Wheelchair-accessible alpine experience with stunning mountain views",
      accessibility: "Barrier-free circular path with assistance available",
      price: "CHF 45 (including cable car)",
      timeSlots: ["Cable car runs 8:30 AM - 5:00 PM"]
    }
  ];

  const roomPersonalization = [
    {
      category: "Medical Care & Safety",
      items: [
        { name: "Dedicated insulin storage mini-fridge (2-8°C)", description: "Temperature-controlled storage for medication safety", selected: false },
        { name: "Blood glucose testing kit storage organizer", description: "Organized storage for diabetes management supplies", selected: false },
        { name: "24/7 medical concierge card", description: "Direct contact for diabetes emergencies", selected: false },
        { name: "Diabetic-friendly snack selection", description: "Low-carb, sugar-free options available", selected: false },
        { name: "Emergency glucose tablets", description: "Quick-acting glucose for emergency situations", selected: false }
      ]
    },
    {
      category: "Accessibility Enhancements",
      items: [
        { name: "Enhanced bathroom grab bars with non-slip coating", description: "Additional safety features for bathroom access", selected: false },
        { name: "Shower chair with back support", description: "Comfortable seating for longer shower sessions", selected: false },
        { name: "Reacher/grabber tool", description: "Assistive device for accessing high shelves", selected: false },
        { name: "Emergency call button extension cord", description: "Portable emergency access throughout room", selected: false },
        { name: "Wheelchair accessible room service table", description: "Adjustable height table for comfortable dining", selected: false }
      ]
    },
    {
      category: "Comfort & Wellness",
      items: [
        { name: "Essential oil diffuser with relaxation scents", description: "Aromatherapy for spa-like experience", selected: false },
        { name: "Compression socks for circulation", description: "Medical-grade support for extended sitting", selected: false },
        { name: "Ergonomic lumbar support cushion", description: "Additional comfort for wheelchair users", selected: false },
        { name: "Italian and Japanese tea selection", description: "Premium teas reflecting cuisine preferences", selected: false },
        { name: "Architecture and history books about Zurich", description: "Local interest reading material", selected: false }
      ]
    },
    {
      category: "Dietary Support",
      items: [
        { name: "Gluten-free welcome amenities", description: "Certified gluten-free snacks and treats", selected: false },
        { name: "Vegetarian cookbook featuring Swiss recipes", description: "Local vegetarian cuisine inspiration", selected: false },
        { name: "List of nearby gluten-free grocers", description: "Resource for dietary shopping needs", selected: false },
        { name: "Diabetic-friendly room service menu", description: "Specialized dining options available 24/7", selected: false }
      ]
    }
  ];

  const carRentalOptions = [
    {
      id: 1,
      company: "Hertz Switzerland",
      vehicleType: "Electric SUV",
      models: ["Tesla Model X", "BMW iX", "Volkswagen ID.4"],
      features: [
        "Shell Recharge network access (300,000+ stations)",
        "Wheelchair accessible models available",
        "Full insurance coverage included",
        "Free charging at approved stations"
      ],
      accessibility: "Tesla Model X has falcon wing doors for easier wheelchair transfer",
      price: "From CHF 89/day",
      location: "Zurich Airport & City Center",
      bookingBenefit: "Frequent Traveler Plus 15% discount applies"
    },
    {
      id: 2,
      company: "Avis Switzerland",
      vehicleType: "Electric/Hybrid SUV",
      models: ["Tesla Model Y", "Audi e-tron", "Mercedes EQC"],
      features: [
        "500,000+ charging points across Europe",
        "Real-time charging station availability",
        "Eco-friendly vehicle options",
        "24/7 roadside assistance"
      ],
      accessibility: "Hand controls available for drivers with mobility needs",
      price: "From CHF 85/day",
      location: "Multiple Zurich locations",
      bookingBenefit: "Special mobility equipment at no extra charge"
    }
  ];

  const placesToVisit = [
    {
      id: 1,
      name: "Fondation Beyeler Art Museum",
      type: "Museum",
      distance: "45 minutes from Zurich",
      highlights: ["Wheelchair accessible", "World-class art collection", "Beautiful architecture"],
      accessibility: "Fully accessible with special tours for visitors with disabilities",
      relevance: "Combines art appreciation with architectural interest",
      admissionBenefit: "Free admission for care partners"
    },
    {
      id: 2,
      name: "Rhine Falls with Accessible Viewing Platform",
      type: "Natural Wonder",
      distance: "1 hour from Zurich",
      highlights: ["Europe's most powerful waterfall", "Wheelchair accessible platforms", "Boat trips available"],
      accessibility: "Accessible viewing platforms and elevator access",
      relevance: "Stunning natural beauty with full accessibility",
      admissionBenefit: "Reduced admission with disability ID"
    },
    {
      id: 3,
      name: "Lucerne Historic Old Town",
      type: "Historic Site",
      distance: "1 hour from Zurich",
      highlights: ["Medieval architecture", "Chapel Bridge", "Accessible public transport"],
      accessibility: "Many historic sites have been made wheelchair accessible",
      relevance: "Perfect for history and architecture enthusiasts",
      admissionBenefit: "Free guided tours for visitors with special needs"
    }
  ];

  const handleItemSelection = (category: string, itemId: number) => {
    setSelectedItems((prev: any) => ({
      ...prev,
      [category]: prev[category].includes(itemId)
        ? prev[category].filter((id: number) => id !== itemId)
        : [...prev[category], itemId]
    }));
  };

  return (
    <Container fluid className="py-4 bg-light">
      <Row className="justify-content-center">
        <Col lg={10}>
          {/* Header */}
          <Card className="shadow-lg border-0 mb-4">
            <Card.Header className="bg-primary text-white p-4">
              <Row className="align-items-center">
                <Col md={8}>
                  <h2 className="mb-2 fw-bold">Welcome, {displayName}!</h2>
                  <p className="mb-0 fs-5">Your personalized pre-check-in experience is ready</p>
                  <div className="mt-3 d-flex gap-3 flex-wrap">
                    <Badge  className="px-3 py-2">
                      <i className="fas fa-calendar me-2"></i>
                      Check-in: {guestProfile.checkIn}
                    </Badge>
                    <Badge  className="px-3 py-2">
                      <i className="fas fa-moon me-2"></i>
                      {guestProfile.nights} nights
                    </Badge>
                  </div>
                </Col>
                <Col md={4} className="text-end">
                  <div className="text-light">
                    <h5>Quick Profile</h5>
                    <small>{guestProfile.accessibility}</small><br/>
                    <small>{guestProfile.medical}</small><br/>
                    <small>{guestProfile.dietary.join(", ")}</small>
                  </div>
                </Col>
              </Row>
            </Card.Header>
          </Card>

          {/* Navigation Tabs */}
          <Tab.Container
            activeKey={activeTab}
            onSelect={(k) => {
              if (k) setActiveTab(k);
            }}
          >
            <Card className="shadow border-0">
              <Card.Header className="p-0">
                <Nav variant="tabs" className="nav-fill">
                  <Nav.Item>
                    <Nav.Link eventKey="overview" className="py-3">
                      <i className="fas fa-home me-2"></i>Overview
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="events" className="py-3">
                      <i className="fas fa-calendar-star me-2"></i>Local Events
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="dining" className="py-3">
                      <i className="fas fa-utensils me-2"></i>Dining
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="activities" className="py-3">
                      <i className="fas fa-map-marked-alt me-2"></i>Activities
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="room" className="py-3">
                      <i className="fas fa-bed me-2"></i>Room Setup
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="transport" className="py-3">
                      <i className="fas fa-car me-2"></i>Transport
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </Card.Header>

              <Card.Body className="p-4">
                <Tab.Content>
                  {/* Overview Tab */}
                  <Tab.Pane eventKey="overview">
                    <Row className="g-4">
                      <Col md={6}>
                        <Alert variant="success" className="border-0">
                          <Alert.Heading>
                            <i className="fas fa-check-circle me-2"></i>
                            Everything is Ready!
                          </Alert.Heading>
                          <p>We've curated a perfect experience based on your preferences for accessibility, dining, and cultural interests. Your room has been specially prepared with medical care considerations.</p>
                        </Alert>
                      </Col>
                      <Col md={6}>
                        <Card className="h-100 bg-info text-white">
                          <Card.Body>
                            <h5><i className="fas fa-concierge-bell me-2"></i>Personal Concierge</h5>
                            <p>Available 24/7 for accessibility assistance, medical support, and dining recommendations.</p>
                            <Button variant="light" size="sm">Contact Now</Button>
                          </Card.Body>
                        </Card>
                      </Col>
                      
                      <Col md={4}>
                        <Card className="text-center h-100">
                          <Card.Body>
                            <i className="fas fa-heartbeat fa-3x text-danger mb-3"></i>
                            <h6>Medical Support Ready</h6>
                            <small className="text-muted">Insulin storage, diabetic meals, 24/7 medical assistance</small>
                          </Card.Body>
                        </Card>
                      </Col>
                      
                      <Col md={4}>
                        <Card className="text-center h-100">
                          <Card.Body>
                            <i className="fas fa-universal-access fa-3x text-primary mb-3"></i>
                            <h6>Full Accessibility</h6>
                            <small className="text-muted">Wheelchair accessible room, bathroom, and all hotel facilities</small>
                          </Card.Body>
                        </Card>
                      </Col>
                      
                      <Col md={4}>
                        <Card className="text-center h-100">
                          <Card.Body>
                            <i className="fas fa-leaf fa-3x text-success mb-3"></i>
                            <h6>Dietary Care</h6>
                            <small className="text-muted">Certified vegetarian & gluten-free kitchen and dining options</small>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </Tab.Pane>

                  {/* Local Events Tab */}
                  <Tab.Pane eventKey="events">
                    <h4 className="mb-4">Events During Your Stay</h4>
                    <Row className="g-4">
                      {currentEvents.map(event => (
                        <Col lg={4} key={event.id}>
                          <Card className="h-100 border-primary">
                            <Card.Header className="bg-primary text-white">
                              <h6 className="mb-0">{event.name}</h6>
                              <small>{event.dates}</small>
                            </Card.Header>
                            <Card.Body>
                              <p className="small">{event.description}</p>
                              <Badge className="mb-2 w-100">
                                <i className="fas fa-wheelchair me-2"></i>
                                {event.accessibility}
                              </Badge>
                              <p className="small text-muted mb-2">
                                <i className="fas fa-star me-2 text-warning"></i>
                                {event.relevance}
                              </p>
                              <div className="d-flex justify-content-between align-items-center">
                                <span className="fw-bold text-success">{event.price}</span>
                                <Button variant="outline-primary" size="sm">Book Now</Button>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </Tab.Pane>

                  {/* Dining Tab */}
                  <Tab.Pane eventKey="dining">
                    <h4 className="mb-4">Curated Restaurant Recommendations</h4>
                    <Row className="g-4">
                      {restaurantRecommendations.map(restaurant => (
                        <Col lg={4} key={restaurant.id}>
                          <Card className="h-100">
                            <Card.Header className="d-flex justify-content-between align-items-center">
                              <div>
                                <h6 className="mb-0">{restaurant.name}</h6>
                                <small className="text-muted">{restaurant.cuisine}</small>
                              </div>
                              <div className="text-end">
                                <div className="text-warning">
                                  {"★".repeat(Math.floor(restaurant.rating))} {restaurant.rating}
                                </div>
                                <small className="text-muted">{restaurant.priceRange}</small>
                              </div>
                            </Card.Header>
                            <Card.Body>
                              <p className="small text-muted mb-2">
                                <i className="fas fa-map-marker-alt me-2"></i>
                                {restaurant.distance}
                              </p>
                              <div className="mb-3">
                                {restaurant.features.map((feature, index) => (
                                  <Badge key={index} className="me-1 mb-1">
                                    {feature}
                                  </Badge>
                                ))}
                              </div>
                              <ListGroup variant="flush" className="mb-3">
                                {restaurant.specialties.slice(0, 3).map((specialty, index) => (
                                  <ListGroup.Item key={index} className="px-0 py-1 small">
                                    <i className="fas fa-check text-success me-2"></i>
                                    {specialty}
                                  </ListGroup.Item>
                                ))}
                              </ListGroup>
                              <div className="d-grid gap-2">
                                {restaurant.reservationNeeded && (
                                  <Alert variant="warning" className="py-2 mb-2">
                                    <small><i className="fas fa-clock me-2"></i>Reservation recommended</small>
                                  </Alert>
                                )}
                                <Button variant="primary" size="sm">Make Reservation</Button>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </Tab.Pane>

                  {/* Activities Tab */}
                  <Tab.Pane eventKey="activities">
                    <Row className="mb-4">
                      <Col>
                        <h4>Personalized Activity Recommendations</h4>
                        <p className="text-muted">Activities selected based on your interests in history, architecture, and accessibility needs.</p>
                      </Col>
                    </Row>
                    <Row className="g-4">
                      {activityRecommendations.map(activity => (
                        <Col lg={6} key={activity.id}>
                          <Card className="h-100 border-info">
                            <Card.Header className="bg-info text-white">
                              <div className="d-flex justify-content-between">
                                <div>
                                  <h6 className="mb-0">{activity.name}</h6>
                                  <small>{activity.type} • {activity.duration}</small>
                                </div>
                                <span className="fw-bold">{activity.price}</span>
                              </div>
                            </Card.Header>
                            <Card.Body>
                              <p>{activity.description}</p>
                              <Alert variant="success" className="py-2 mb-3">
                                <small>
                                  <i className="fas fa-universal-access me-2"></i>
                                  {activity.accessibility}
                                </small>
                              </Alert>
                              <div className="mb-3">
                                <h6>Features:</h6>
                                {activity.features.map((feature, index) => (
                                  <Badge key={index}  className="me-1 mb-1">
                                    {feature}
                                  </Badge>
                                ))}
                              </div>
                              <div className="mb-3">
                                <h6>Available Times:</h6>
                                {activity.timeSlots.map((time, index) => (
                                  <Button key={index} variant="outline-secondary" size="sm" className="me-2 mb-2">
                                    {time}
                                  </Button>
                                ))}
                              </div>
                              <Button variant="primary" className="w-100">Book Activity</Button>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>

                    {/* Places to Visit */}
                    <Row className="mt-5">
                      <Col>
                        <h4>Recommended Day Trips</h4>
                        <p className="text-muted">Accessible destinations perfect for your interests, all within easy reach.</p>
                      </Col>
                    </Row>
                    <Row className="g-3">
                      {placesToVisit.map(place => (
                        <Col lg={4} key={place.id}>
                          <Card className="h-100">
                            <Card.Body>
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <h6 className="mb-0">{place.name}</h6>
                                <Badge >{place.type}</Badge>
                              </div>
                              <p className="small text-muted mb-2">
                                <i className="fas fa-clock me-2"></i>{place.distance}
                              </p>
                              <div className="mb-2">
                                {place.highlights.map((highlight, index) => (
                                  <small key={index} className="text-success d-block">
                                    <i className="fas fa-check me-2"></i>{highlight}
                                  </small>
                                ))}
                              </div>
                              <Alert variant="info" className="py-2 mb-2">
                                <small>{place.accessibility}</small>
                              </Alert>
                              <p className="small">{place.relevance}</p>
                              <Badge className="w-100">
                                {place.admissionBenefit}
                              </Badge>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </Tab.Pane>

                  {/* Room Setup Tab */}
                  <Tab.Pane eventKey="room">
                    <Row className="mb-4">
                      <Col>
                        <h4>Personalize Your Room Setup</h4>
                        <p className="text-muted">Select additional items to have ready in your room upon arrival. All selections are complimentary.</p>
                      </Col>
                    </Row>
                    
                    <Accordion defaultActiveKey="0">
                      {roomPersonalization.map((category, categoryIndex) => (
                        <Card >
                          <Card.Header style={{textAlign: "left"}}>
                          <Accordion.Toggle className="d-flex justify-content-between w-100 me-3" eventKey={categoryIndex.toString()} style={{textAlign: "left", cursor: "pointer", border: "none", backgroundColor: "transparent"}}>
                              <span className="fw-bold">{category.category}</span>
                              <Badge style={{backgroundColor: "#d4edda", color: "#155724"}} className="fs-6">
                                {selectedItems.roomItems.filter((id:string) => 
                                  category.items.some(item => item.name === id)
                                ).length} selected
                              </Badge>
                            </Accordion.Toggle>
                        </Card.Header>

                          <Accordion.Collapse key={categoryIndex} eventKey={categoryIndex.toString()}>
                            <Card.Body style={{textAlign: "left"}}>
                              {category.items.map((item: any, itemIndex) => (
                                <Col md={6} key={itemIndex}>
                                  <Card 
                                    className={`h-100 cursor-pointer ${selectedItems["roomItems"].includes(item.name) ? 'border-success bg-light' : ''}`}
                                    onClick={() => handleItemSelection('roomItems', item.name)}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    <Card.Body className="p-3">
                                      <div className="d-flex justify-content-between align-items-start mb-2">
                                        <h6 className="mb-0 flex-grow-1">{item.name}</h6>
                                        <div className="ms-2">
                                          {selectedItems["roomItems"].includes(item.name) ? (
                                            <i className="fas fa-check-circle text-success"></i>
                                          ) : (
                                            <i className="far fa-circle text-muted"></i>
                                          )}
                                        </div>
                                      </div>
                                      <p className="small text-muted mb-0">{item.description}</p>
                                    </Card.Body>
                                  </Card>
                                </Col>
                              ))}
                            </Card.Body>
                          </Accordion.Collapse>
                        </Card>
                      ))}
                    </Accordion>

                    <Alert variant="success" className="mt-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>Total Selected Items: {selectedItems.roomItems.length}</strong>
                          <p className="mb-0 small">All personalization items will be ready 2 hours before your check-in.</p>
                        </div>
                        <Button variant="success">Confirm Room Setup</Button>
                      </div>
                    </Alert>
                  </Tab.Pane>

                  {/* Transport Tab */}
                  <Tab.Pane eventKey="transport">
                    <Row className="mb-4">
                      <Col>
                        <h4>Electric Vehicle Rental Options</h4>
                        <p className="text-muted">Based on your preference for electric SUVs and environmental consciousness.</p>
                      </Col>
                    </Row>

                    <Row className="g-4">
                      {carRentalOptions.map(rental => (
                        <Col lg={6} key={rental.id}>
                          <Card className="h-100 border-success">
                            <Card.Header className="bg-success text-white">
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <h5 className="mb-0">{rental.company}</h5>
                                  <small>{rental.vehicleType}</small>
                                </div>
                                <Badge className="fs-6">
                                  {rental.price}
                                </Badge>
                              </div>
                            </Card.Header>
                            <Card.Body>
                              <div className="mb-3">
                                <h6>Available Models:</h6>
                                {rental.models.map((model, index) => (
                                  <Badge key={index}  className="me-2 mb-2">
                                    {model}
                                  </Badge>
                                ))}
                              </div>
                              
                              <div className="mb-3">
                                <h6>Features:</h6>
                                <ListGroup variant="flush">
                                  {rental.features.map((feature, index) => (
                                    <ListGroup.Item key={index} className="px-0 py-1">
                                      <i className="fas fa-check text-success me-2"></i>
                                      <small>{feature}</small>
                                    </ListGroup.Item>
                                  ))}
                                </ListGroup>
                              </div>

                              <Alert variant="info" className="py-2 mb-3">
                                <small>
                                  <strong>Accessibility:</strong> {rental.accessibility}
                                </small>
                              </Alert>

                              <Alert variant="success" className="py-2 mb-3">
                                <small>
                                  <i className="fas fa-star me-2"></i>
                                  <strong>Your Benefit:</strong> {rental.bookingBenefit}
                                </small>
                              </Alert>

                              <div className="mb-3">
                                <small className="text-muted">
                                  <i className="fas fa-map-marker-alt me-2"></i>
                                  Pickup: {rental.location}
                                </small>
                              </div>

                              <div className="d-grid gap-2">
                                <Button 
                                  variant={selectedItems.carRental === rental.id ? "success" : "outline-primary"}
                                  onClick={() => setSelectedItems((prev: any) => Object.assign(prev, {carRental: rental.id}))}
                                >
                                  {selectedItems.carRental === rental.id ? (
                                    <>
                                      <i className="fas fa-check me-2"></i>Selected
                                    </>
                                  ) : (
                                    "Select This Option"
                                  )}
                                </Button>
                                <Button variant="link" size="sm">View Full Details</Button>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>

                    {selectedItems.carRental && (
                      <Alert variant="success" className="mt-4">
                        <Alert.Heading>
                          <i className="fas fa-car me-2"></i>
                          Rental Reserved!
                        </Alert.Heading>
                        <p>Your electric vehicle will be ready for pickup upon your arrival. We'll send you the confirmation details and pickup instructions.</p>
                        <div className="d-flex gap-2">
                          <Button variant="success" size="sm">Confirm Booking</Button>
                          <Button variant="outline-success" size="sm">Modify Selection</Button>
                        </div>
                      </Alert>
                    )}
                  </Tab.Pane>
                </Tab.Content>
              </Card.Body>
            </Card>
          </Tab.Container>

          {/* Final Summary Card */}
          <Card className="shadow mt-4 border-0">
            <Card.Footer className="bg-primary text-white p-4">
              <Row className="align-items-center">
                <Col md={8}>
                  <h5 className="mb-2">Your Personalized Experience Summary</h5>
                  <div className="d-flex gap-4 flex-wrap">
                    <small>
                      <i className="fas fa-bed me-2"></i>
                      {selectedItems.roomItems.length} room items selected
                    </small>
                    <small>
                      <i className="fas fa-car me-2"></i>
                      {selectedItems.carRental ? 'Vehicle reserved' : 'Vehicle pending'}
                    </small>
                    <small>
                      <i className="fas fa-concierge-bell me-2"></i>
                      Personal concierge assigned
                    </small>
                  </div>
                </Col>
                <Col md={4} className="text-md-end">
                  <Button variant="light" size="lg" className="fw-bold">
                    <i className="fas fa-save me-2"></i>
                    Save All Preferences
                  </Button>
                </Col>
              </Row>
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default PersonalizedPreCheckinExperience;
